package com.heelion.farmafreelance.ui

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.launch
import com.heelion.farmafreelance.api.FarmaFreelanceApi
import com.heelion.farmafreelance.models.*
import com.heelion.farmafreelance.agent.PreferenzePromemoria
import com.heelion.farmafreelance.utils.*

import com.heelion.farmafreelance.services.LocationManager
import kotlinx.datetime.*
import kotlinx.coroutines.flow.collectLatest

class AuthViewModel : ViewModel() {
    private val api = FarmaFreelanceApi()

    // --- LOGICA GEOGRAFICA ---
    var isLocationDataLoaded by mutableStateOf(false)
    var listaRegioni by mutableStateOf<List<String>>(emptyList())
    var listaProvince by mutableStateOf<List<String>>(emptyList())
    var listaComuni by mutableStateOf<List<String>>(emptyList())
    var listaCap by mutableStateOf<List<String>>(emptyList())

    // Mapping per cleanup selezioni
    var mappingProvinciaRegione = mutableMapOf<String, String>()
    var mappingComuneProvincia = mutableMapOf<String, String>()

    init {
        loadRegioni()
        // Sessione rifiutata dal server (scaduta o chiusa da un cambio password): si torna al login.
        FarmaFreelanceApi.allaSessioneScaduta = {
            viewModelScope.launch {
                if (currentUserEmail != null) {
                    logout()
                    errorMessage = "Sessione scaduta: accedi di nuovo"
                }
            }
        }
    }

    fun loadRegioni() {
        viewModelScope.launch {
            println("DEBUG: Tentativo caricamento regioni...")
            try {
                val regioni = api.getRegioni()
                if (regioni.isNotEmpty()) {
                    listaRegioni = regioni
                    isLocationDataLoaded = true
                    println("DEBUG: Caricate ${regioni.size} regioni")
                } else {
                    println("DEBUG: Lista regioni ricevuta vuota dal server")
                }
            } catch (e: Exception) {
                println("DEBUG: Error loading regions: ${e.message}")
            }
        }
    }

    fun updateProvincePerRegione(regione: String) {
        viewModelScope.launch {
            listaProvince = api.getProvince(regione)
            // Selezionando una nuova regione singola, resettiamo comuni e cap caricati
            listaComuni = emptyList()
            listaCap = emptyList()
        }
    }

    fun updateProvincePerRegioni(regioni: List<String>) {
        listaProvince = emptyList()
        viewModelScope.launch {
            if (regioni.isEmpty()) {
                // Già svuotata sopra
            } else {
                val allProvince = mutableListOf<String>()
                regioni.forEach { r ->
                    val provs = api.getProvince(r)
                    provs.forEach { p -> mappingProvinciaRegione[p] = r }
                    allProvince.addAll(provs)
                }
                listaProvince = allProvince.distinct().sorted()
            }
            listaComuni = emptyList()
            listaCap = emptyList()
        }
    }

    fun updateComuniPerProvince(province: List<String>) {
        listaComuni = emptyList()
        viewModelScope.launch {
            if (province.isEmpty()) {
                // Già svuotata sopra
            } else {
                val allComuni = mutableListOf<String>()
                province.forEach { p ->
                    val coms = api.getComuni(p)
                    coms.forEach { c -> mappingComuneProvincia[c] = p }
                    allComuni.addAll(coms)
                }
                listaComuni = allComuni.distinct().sorted()
            }
            listaCap = emptyList()
        }
    }

    fun updateComuniPerProvincia(provincia: String) {
        viewModelScope.launch {
            listaComuni = api.getComuni(provincia)
            listaCap = emptyList()
        }
    }

    fun updateCapPerComune(comune: String) {
        viewModelScope.launch {
            listaCap = api.getCap(comune)
        }
    }

    suspend fun searchComuni(query: String): List<String> {
        return api.searchComuni(query)
    }
    // --------------------------

    var isLoading by mutableStateOf(false)
    var errorMessage by mutableStateOf<String?>(null)
    var registrationSuccess by mutableStateOf(false)
    
    var rememberMe by mutableStateOf(false)
    var savedEmail by mutableStateOf("")

    fun updateRememberMe(value: Boolean) {
        rememberMe = value
        storage?.saveString("remember_me", value.toString())
        if (!value) {
            // Se disabilitato, cancella subito tutto
            storage?.saveString("saved_email", "")
            storage?.saveString("saved_token", "")
            savedEmail = ""
        }
    }
    
    var currentUserRole by mutableStateOf<UserRole?>(null)
    var currentUserEmail by mutableStateOf<String?>(null)
    var currentUserFarmacista by mutableStateOf<Farmacista?>(null)
    var currentUserFarmacia by mutableStateOf<Farmacia?>(null)
    var currentUserInfermiere by mutableStateOf<Infermiere?>(null)

    /** Il professionista collegato (farmacista o infermiere), per tutto quello che hanno in comune. */
    val currentUserProfessionista: Professionista?
        get() = currentUserFarmacista ?: currentUserInfermiere

    /** Chi lavora a turno (farmacista o infermiere) invece di una farmacia. */
    val isProfessionista: Boolean
        get() = currentUserRole?.isProfessionista == true

    /** Id numerico dell'utente collegato, nella tabella del suo ruolo. */
    private val mioId: String?
        get() = currentUserFarmacia?.id ?: currentUserProfessionista?.id
    var currentMatches by mutableStateOf<List<Match>>(emptyList())
    var userReputation by mutableStateOf(UserReputation())
    var currentPreferiti by mutableStateOf<List<Preferito>>(emptyList())
    var notifications by mutableStateOf<List<Notification>>(emptyList())
    var unreadNotificationsCount by mutableStateOf(0)
    var actionErrorMessage by mutableStateOf<String?>(null)
    var successMessage by mutableStateOf<String?>(null)
    
    // Cache per i dettagli degli annunci confermati (per controllo sovrapposizioni)
    var confirmedShiftsDetails by mutableStateOf<List<AnnuncioTurno>>(emptyList())
    
    fun checkConflict(ad: AnnuncioTurno): Instant? {
        if (!isProfessionista) return null
        
        confirmedShiftsDetails.forEach { confirmed ->
            if (ad.id != confirmed.id && TimeUtils.isOverlap(
                ad.dataTurno, ad.oraInizio, ad.oraFine,
                confirmed.dataTurno, confirmed.oraInizio, confirmed.oraFine
            )) {
                return confirmed.dataTurno
            }
        }
        return null
    }
    
    // --- STATO ABBONAMENTO ---
    var subscriptionStatus: UserStatus by mutableStateOf(UserStatus(userId = "", isPremium = false, subscriptionType = SubscriptionType.FREE))

    // Tutte le funzioni sbloccate: sempre finché l'app è gratuita, poi solo con un Premium valido
    val accessoCompleto: Boolean get() = !com.heelion.farmafreelance.ABBONAMENTI_ATTIVI || subscriptionStatus.isValidPremium
    
    fun fetchSubscriptionStatus() {
        // App gratuita: niente chiamata a FarPayment e niente messaggi su "Account Base"/Premium.
        if (!com.heelion.farmafreelance.ABBONAMENTI_ATTIVI) return
        val email = currentUserEmail ?: return
        val role = currentUserRole ?: return
        val myId = getUnivocoUtenteId() ?: email
        
        viewModelScope.launch {
            isLoading = true
            successMessage = null
            actionErrorMessage = null
            val myId = getUnivocoUtenteId() ?: email
            val targetUrl = "${com.heelion.farmafreelance.PAYMENT_API_URL}/v1/status/${com.heelion.farmafreelance.PAYMENT_APP_ID}/$myId"
            println("DEBUG: Chiamata a: $targetUrl")
            
            try {
                val status = api.getSubscriptionStatus(myId)
                subscriptionStatus = status
                
                // Salva ultimo stato noto
                storage?.saveString("last_subscription_type", status.subscriptionType.name)
                storage?.saveString("last_is_premium", status.isPremium.toString())
                status.userRole?.let { storage?.saveString("last_user_role", it.name) }
                
                if (status.isPremium) {
                    val expDate = status.expiryDate
                    val expiryInfo = if (expDate != null) {
                        val expiry = Instant.fromEpochMilliseconds(expDate).toLocalDateTime(TimeZone.currentSystemDefault()).date
                        " fino al $expiry"
                    } else ""
                    successMessage = "Abbonamento ${status.subscriptionType} attivo$expiryInfo!"
                    println("DEBUG: Abbonamento PREMIUM trovato: ${status.subscriptionType} fino a $expiryInfo")
                } else {
                    successMessage = "Stato aggiornato: Account Base"
                    println("DEBUG: L'utente risulta in versione BASE")
                }
            } catch (e: Exception) {
                actionErrorMessage = "Errore connessione Hub: ${e.message}"
                println("DEBUG: ERRORE CRITICO verifica abbonamento: ${e.message}")
                e.printStackTrace()
            } finally {
                isLoading = false
            }
        }
    }
    
    private var storage: SettingsStorage? = null
    var selectedThemeColor by mutableStateOf(AppThemeColor.GREEN)
    var selectedLanguage by mutableStateOf("it")

    fun initStorage(context: Any?) {
        if (storage == null) {
            storage = createSettingsStorage(context)
            
            // Carica colore
            val savedColorName = storage?.getString("theme_color", AppThemeColor.GREEN.name)
            selectedThemeColor = try {
                AppThemeColor.valueOf(savedColorName ?: AppThemeColor.GREEN.name)
            } catch (e: Exception) {
                AppThemeColor.GREEN
            }
            
            // Carica lingua
            selectedLanguage = storage?.getString("app_language", "it") ?: "it"

            // Voce di Farmy scelta nel selettore dell'assistente ("" = automatica)
            voceFarmy = storage?.getString("voce_farmy", "") ?: ""

            // Spazio (farmacisti o infermieri) scelto l'ultima volta dalla farmacia.
            val professioneSalvata = storage?.getString("professione_attiva", "")
            professioneAttiva = Professione.entries.firstOrNull { it.name == professioneSalvata } ?: Professione.FARMACISTA

            // Promemoria di Farmy scelti dall'utente (tipi attivi e orari)
            preferenzePromemoria = PreferenzePromemoria.decodifica(storage?.getString("promemoria_farmy", ""))
            
            // Carica abbonamento (ultimo stato noto)
            val lastTypeStr = storage?.getString("last_subscription_type", "FREE") ?: "FREE"
            val lastType = try { SubscriptionType.valueOf(lastTypeStr) } catch(e: Exception) { SubscriptionType.FREE }
            val lastIsPremium = storage?.getString("last_is_premium", "false") == "true"
            val lastRoleStr = storage?.getString("last_user_role", "")
            val lastRole = try { if (!lastRoleStr.isNullOrEmpty()) UserRole.valueOf(lastRoleStr) else null } catch(e: Exception) { null }
            
            rememberMe = storage?.getString("remember_me", "false") == "true"
            savedEmail = storage?.getString("saved_email", "") ?: ""
            // "Ricordami" ora tiene il token di sessione, non più la password. Le versioni
            // precedenti salvavano la password: si usa un'ultima volta per entrare e si cancella.
            val vecchiaPassword = storage?.getString("saved_password", "") ?: ""
            storage?.saveString("saved_password", "")
            val tokenSalvato = storage?.getString("saved_token", "") ?: ""

            if (rememberMe && tokenSalvato.isNotBlank()) {
                riprendiSessione(tokenSalvato)
            } else if (rememberMe && savedEmail.isNotBlank() && vecchiaPassword.isNotBlank()) {
                login(LoginRequest(savedEmail, vecchiaPassword))
            }

            subscriptionStatus = UserStatus(
                userId = "", 
                isPremium = lastIsPremium, 
                subscriptionType = lastType,
                userRole = lastRole
            )
        }
    }

    /** Id della voce di Farmy scelta dall'utente ("" = automatica). */
    var voceFarmy by mutableStateOf("")

    /** Promemoria di Farmy attivi e orari: li cambia l'utente parlando con Farmy o con gli interruttori. */
    var preferenzePromemoria by mutableStateOf(PreferenzePromemoria())

    fun aggiornaPreferenzePromemoria(p: PreferenzePromemoria) {
        preferenzePromemoria = p
        storage?.saveString("promemoria_farmy", p.codifica())
        inviaTokenPush() // il server deve sapere quali push non mandare (es. le disdette)
    }

    // --- FARMACIA: FARMACISTI E INFERMIERI SEPARATI ---

    /** Spazio scelto dalla farmacia nel selettore in alto: Home, turni e ricerca mostrano solo questa professione. */
    var professioneAttiva by mutableStateOf(Professione.FARMACISTA)
        private set

    fun scegliProfessione(p: Professione) {
        professioneAttiva = p
        storage?.saveString("professione_attiva", p.name)
    }

    /**
     * La farmacia lavora anche con gli infermieri: solo allora vede il selettore e la parte infermieri.
     * Se non l'ha mai scelto (farmacie iscritte prima) vale sì solo se ha già avuto a che fare con infermieri.
     */
    val infermieriAttivi: Boolean
        get() = currentUserRole == UserRole.FARMACIA &&
            (currentUserFarmacia?.serviziInfermieristici ?: currentMatches.any { it.professione == Professione.INFERMIERE })

    /** La professione che la farmacia sta guardando: senza infermieri attivi, sempre i farmacisti. */
    val professioneVisibile: Professione
        get() = if (infermieriAttivi) professioneAttiva else Professione.FARMACISTA

    /** true se le cose di questa professione vanno mostrate (per i professionisti sempre: vedono solo le loro). */
    fun visibile(p: Professione): Boolean = currentUserRole != UserRole.FARMACIA || p == professioneVisibile

    /** Per il numerino del selettore: proposte ricevute e accettazioni da confermare di quella professione. */
    fun daGuardare(p: Professione): Int = currentMatches.count {
        it.professione == p && (
            (it.stato == MatchStato.PROPOSTO && it.propostoDa != CreatoreTipo.FARMACIA) ||
                (it.stato == MatchStato.ACCETTATO && it.propostoDa == CreatoreTipo.FARMACIA)
            )
    }

    /** Interruttore "Servizi infermieristici" nel profilo della farmacia; spegnendolo si torna ai farmacisti. */
    fun impostaServiziInfermieristici(attivi: Boolean) {
        val farmacia = currentUserFarmacia ?: return
        currentUserFarmacia = farmacia.copy(serviziInfermieristici = attivi)
        if (!attivi) scegliProfessione(Professione.FARMACISTA)
        updateFarmaciaProfile(farmacia.copy(serviziInfermieristici = attivi))
    }

    fun updateVoceFarmy(id: String) {
        voceFarmy = id
        storage?.saveString("voce_farmy", id)
    }

    fun updateThemeColor(color: AppThemeColor) {
        selectedThemeColor = color
        storage?.saveString("theme_color", color.name)
    }

    fun updateLanguage(lang: String) {
        selectedLanguage = lang
        storage?.saveString("app_language", lang)
    }

    fun sendDirectProposal(targetId: String, targetRuolo: String, message: String, onResult: (Boolean) -> Unit) {
        val myEmail = currentUserEmail ?: return
        val targetUnivoco = "${targetRuolo}_$targetId"
        viewModelScope.launch {
            val success = api.sendDirectProposal(targetUnivoco, targetRuolo, message, myEmail)
            onResult(success)
            if (success) refreshUserData()
        }
    }

    /** Invita un professionista su un turno della farmacia: null se è andato bene, altrimenti il messaggio d'errore. */
    suspend fun invitaSuTurno(annuncioId: String, professionistaId: String): String? {
        val myEmail = currentUserEmail ?: return "Accedi di nuovo"
        val errore = api.invitaSuTurno(annuncioId, professionistaId, myEmail)
        if (errore == null) refreshUserData()
        return errore
    }

    fun getUnivocoUtenteId(): String? {
        val role = currentUserRole ?: return null
        val numericId = mioId ?: currentUserEmail ?: return null
        // "FARMACISTA_12", "INFERMIERE_3", "FARMACIA_7": come il server salva notifiche e preferiti.
        return "${if (role.isProfessionista) role.name else UserRole.FARMACIA.name}_$numericId"
    }

    // --- "AVVISAMI QUANDO…" (ricerche salvate) ---
    var ricercheSalvate by mutableStateOf<List<RicercaSalvata>>(emptyList())

    suspend fun caricaRicercheSalvate() {
        val myId = getUnivocoUtenteId() ?: return
        api.getRicercheSalvate(myId)?.let { ricercheSalvate = it }
    }

    /** Codice HTTP del salvataggio (201 ok, 409 troppe, 0 rete). */
    suspend fun salvaRicerca(r: RicercaSalvata): Int {
        val codice = api.salvaRicerca(r)
        if (codice == 201) caricaRicercheSalvate()
        return codice
    }

    suspend fun cancellaRicerca(id: String): Boolean {
        val myId = getUnivocoUtenteId() ?: return false
        val ok = api.cancellaRicerca(id, myId)
        if (ok) caricaRicercheSalvate()
        return ok
    }

    // --- NOTIFICHE PUSH ---
    /** Token push di questo dispositivo e piattaforma (ANDROID/WEB), dato dalla UI. */
    private var tokenPush: Pair<String, String>? = null

    fun registraTokenPush(token: String, piattaforma: String) {
        tokenPush = token to piattaforma
        inviaTokenPush()
    }

    /**
     * Manda il token al server solo con l'id "vero" dell'utente (profilo caricato): è quello
     * con cui il server salva le notifiche. Se il profilo non c'è ancora, lo rifà
     * [refreshUserData] appena l'ha caricato.
     */
    private fun inviaTokenPush() {
        val (token, piattaforma) = tokenPush ?: return
        if (mioId == null) return
        val myId = getUnivocoUtenteId() ?: return
        viewModelScope.launch {
            val ok = api.registraDispositivoPush(
                DispositivoPush(myId, token, piattaforma, preferenzePromemoria.tipiPushDisattivati)
            )
            println("--- [PUSH] Dispositivo registrato per $myId: $ok ---")
        }
    }

    fun markNotificationsAsRead() {
        val myId = getUnivocoUtenteId() ?: return
        viewModelScope.launch {
            api.markNotificationsAsRead(myId)
            unreadNotificationsCount = 0
            notifications = notifications.map { it.copy(isRead = true) }
        }
    }

    fun deleteNotification(id: String) {
        viewModelScope.launch {
            val success = api.deleteNotification(id)
            if (success) {
                notifications = notifications.filter { it.id != id }
                unreadNotificationsCount = notifications.count { !it.isRead }
            }
        }
    }

    // --- STATO FILTRI HOME ---
    var filtriHome by mutableStateOf<Set<String>>(emptySet())
    var dashboardFiltro by mutableStateOf<String?>(null)

    fun updateDashboardFiltro(filtro: String) {
        dashboardFiltro = if (dashboardFiltro == filtro) null else filtro
    }

    /** Imposta il filtro in modo assoluto (non a toggle): usato dal comando vocale/testuale. */
    fun applicaDashboardFiltro(filtro: String?) {
        dashboardFiltro = filtro
    }

    fun login(request: LoginRequest) {
        viewModelScope.launch {
            isLoading = true
            errorMessage = null
            try {
                val response = api.login(request)
                if (response.success) {
                    entra(response, request.email)
                } else if (response.richiedeCodice && response.tokenTemporaneo != null) {
                    // Password giusta, ma è attiva la verifica in due passaggi: si apre la richiesta del codice.
                    emailDueFattori = request.email
                    tokenDueFattori = response.tokenTemporaneo
                } else {
                    errorMessage = response.message ?: "Credenziali non valide"
                }
            } catch (e: Exception) {
                errorMessage = "Errore di rete: ${e.message}"
            } finally {
                isLoading = false
            }
        }
    }

    // --- Verifica in due passaggi ---

    /** Non null mentre il login aspetta il codice a 6 cifre (token temporaneo, vale 5 minuti). */
    var tokenDueFattori by mutableStateOf<String?>(null)
        private set
    private var emailDueFattori = ""

    /** Secondo passaggio del login. Null se è entrato, altrimenti il messaggio d'errore. */
    suspend fun confermaCodiceLogin(codice: String): String? {
        val temporaneo = tokenDueFattori ?: return "Rifai l'accesso."
        val response = api.loginCodice(temporaneo, codice.trim())
        if (!response.success) return response.message ?: "Codice non valido."
        tokenDueFattori = null
        entra(response, emailDueFattori)
        return null
    }

    fun annullaCodiceLogin() {
        tokenDueFattori = null
    }

    suspend fun statoDueFattori(): DueFattoriStato? = api.statoDueFattori()
    suspend fun iniziaDueFattori(): DueFattoriAvvio? = api.iniziaDueFattori()
    suspend fun confermaDueFattori(codice: String): List<String>? = api.confermaDueFattori(codice.trim())?.codici
    suspend fun disattivaDueFattori(password: String, codice: String): Boolean = api.disattivaDueFattori(password, codice.trim())

    suspend fun consensi(): ConsensiAccount? = api.consensi()
    suspend fun salvaConsensi(marketing: Boolean): Boolean = api.salvaConsensi(ConsensiAccount(marketing))

    /** Chiede l'eliminazione dell'account. Se va a buon fine il token non vale più: poi si chiama [logout]. */
    suspend fun eliminaAccount(password: String): PasswordResetResponse {
        val esito = api.eliminaAccount(password)
        if (esito.success) storage?.saveString("saved_password", "")
        return esito
    }

    /** Accesso automatico ("Ricordami") con il token salvato; se è scaduto si torna al login. */
    private fun riprendiSessione(tokenSalvato: String) {
        viewModelScope.launch {
            isLoading = true
            try {
                FarmaFreelanceApi.token = tokenSalvato
                val response = api.riprendiSessione()
                if (response.success) {
                    entra(response, savedEmail)
                } else {
                    FarmaFreelanceApi.token = null
                    storage?.saveString("saved_token", "")
                }
            } finally {
                isLoading = false
            }
        }
    }

    /** Login riuscito (con password o token salvato): imposta l'utente e la sessione. */
    private suspend fun entra(response: LoginResponse, emailScritta: String) {
        // PROTEZIONE ADMIN
        if (response.role == UserRole.ADMIN && response.email?.lowercase() != "farmafreelance00@gmail.com") {
            errorMessage = "Accesso negato: Area riservata allo sviluppatore."
            return
        }

        FarmaFreelanceApi.token = response.token
        currentUserRole = response.role
        currentUserEmail = response.email

        // Gestione "Ricordami": si tengono email e token, mai la password.
        if (rememberMe) {
            storage?.saveString("remember_me", "true")
            storage?.saveString("saved_email", emailScritta)
            storage?.saveString("saved_token", response.token ?: "")
        } else {
            storage?.saveString("remember_me", "false")
            storage?.saveString("saved_email", "")
            storage?.saveString("saved_token", "")
        }

        // IMPORTANTE: refreshUserData deve caricare l'ID prima di fetchSubscriptionStatus
        refreshUserData().join() // Attendiamo il completamento del caricamento profilo
    }

    var passwordResetLoading by mutableStateOf(false)

    suspend fun recuperaEmail(codice: String): RecuperoEmailResponse = api.recuperaEmail(codice.trim())

    suspend fun chiediRecuperoAccesso(richiesta: RecuperoAccessoRequest): PasswordResetResponse = api.chiediRecuperoAccesso(richiesta)

    suspend fun requestPasswordReset(email: String): PasswordResetResponse {
        passwordResetLoading = true
        return try {
            api.requestPasswordReset(email.trim())
        } catch (e: Exception) {
            PasswordResetResponse(success = false, message = "Errore di rete: ${e.message}")
        } finally {
            passwordResetLoading = false
        }
    }

    suspend fun confirmPasswordReset(email: String, code: String, newPassword: String): PasswordResetResponse {
        passwordResetLoading = true
        return try {
            api.confirmPasswordReset(email.trim(), code.trim(), newPassword)
        } catch (e: Exception) {
            PasswordResetResponse(success = false, message = "Errore di rete: ${e.message}")
        } finally {
            passwordResetLoading = false
        }
    }

    /** Il tour di Farmy va mostrato: solo ai nuovi iscritti, al primo accesso (il server lo sa per account). */
    val deveVedereTour: Boolean
        get() = when (currentUserRole) {
            UserRole.FARMACISTA -> currentUserFarmacista?.tutorialVisto == false
            UserRole.FARMACIA -> currentUserFarmacia?.tutorialVisto == false
            UserRole.INFERMIERE -> currentUserInfermiere?.tutorialVisto == false
            else -> false
        }

    /** Tour visto o saltato: lo si segna sul server e subito anche qui, così non ricompare. */
    fun segnaTourVisto() {
        currentUserFarmacista = currentUserFarmacista?.copy(tutorialVisto = true)
        currentUserFarmacia = currentUserFarmacia?.copy(tutorialVisto = true)
        currentUserInfermiere = currentUserInfermiere?.copy(tutorialVisto = true)
        val email = currentUserEmail ?: return
        val role = currentUserRole?.name ?: return
        viewModelScope.launch { api.segnaTutorialVisto(email, role) }
    }

    /** Lampeggio per qualche secondo insieme al suono: la campanella (notifiche) e i riquadri delle proposte nella Home. */
    var lampeggiaNotifiche by mutableStateOf(false)
    var lampeggiaProposte by mutableStateOf(false)

    /**
     * Ricarica solo le notifiche (controllo periodico con l'app aperta). Se ne arriva una nuova
     * ricarica anche le proposte, così i pulsanti sotto la notifica sono subito giusti.
     */
    suspend fun aggiornaNotifiche() {
        val email = currentUserEmail ?: return
        val role = currentUserRole ?: return
        val myId = getUnivocoUtenteId() ?: return
        try {
            val nuove = api.getNotifications(myId)
            val arrivate = nuove.any { n -> notifications.none { it.id == n.id } }
            notifications = nuove
            unreadNotificationsCount = nuove.count { !it.isRead }
            if (arrivate) currentMatches = api.getMatches(email, role.name)
        } catch (e: Exception) {
            println("DEBUG: Controllo notifiche non riuscito: ${e.message}")
        }
    }

    fun refreshUserData() = viewModelScope.launch {
        val email = currentUserEmail ?: return@launch
        val role = currentUserRole ?: return@launch
        try {
            // Il server gratuito su Render si addormenta: al risveglio (fino a ~1 minuto) la prima
            // richiesta può fallire. Senza profilo l'app userebbe l'email come id (notifiche,
            // preferiti e profilo vuoti), quindi si riprova qualche volta prima di arrendersi.
            for (tentativo in 1..3) {
                when (role) {
                    UserRole.FARMACISTA -> currentUserFarmacista = api.getFarmacistaByEmail(email)
                    UserRole.INFERMIERE -> currentUserInfermiere = api.getInfermiereByEmail(email)
                    UserRole.FARMACIA -> currentUserFarmacia = api.getFarmaciaByEmail(email)
                    UserRole.ADMIN -> {}
                }
                if (mioId != null) break
                println("DEBUG: Profilo non ricevuto (tentativo $tentativo/3), riprovo...")
                kotlinx.coroutines.delay(3000L * tentativo)
            }

            val myId = getUnivocoUtenteId() ?: email
            currentMatches = api.getMatches(email, role.name)
            
            val histCompleted = currentUserProfessionista?.storicoCompletati ?: currentUserFarmacia?.storicoCompletati ?: 0
            val histCancelled = currentUserProfessionista?.storicoAnnullati ?: currentUserFarmacia?.storicoAnnullati ?: 0
            
            userReputation = ReputationUtils.calculateReputation(currentMatches, histCompleted, histCancelled)
            currentPreferiti = api.getPreferiti(myId)
            notifications = api.getNotifications(myId)
            unreadNotificationsCount = notifications.count { !it.isRead }
            inviaTokenPush() // ora l'id dell'utente è noto

            // Carica dettagli dei turni confermati: per il check sovrapposizioni (solo farmacisti,
            // vedi checkConflict) e per l'orario dei promemoria di Farmy (entrambi i ruoli).
            val confirmedIds = currentMatches.filter { it.stato == MatchStato.CONFERMATO }.map { it.annuncioId }
            val details = mutableListOf<AnnuncioTurno>()
            confirmedIds.forEach { id ->
                api.getAnnuncioById(id)?.let { details.add(it) }
            }
            confirmedShiftsDetails = details
            
            // Ora che abbiamo caricato il profilo (e l'ID numerico), verifichiamo l'abbonamento
            fetchSubscriptionStatus()
            
            println("DEBUG: Profile and Subscription loaded for $myId")
        } catch (e: Exception) {
            println("DEBUG: Error refreshing user data: ${e.message}")
        }
    }

    fun respondToMatch(matchId: String, action: String, onResult: (Boolean) -> Unit = {}) {
        val email = currentUserEmail ?: return
        val role = currentUserRole?.name ?: return
        viewModelScope.launch {
            val (success, errorMsg) = api.respondToMatch(matchId, action, email, role)
            if (success) {
                actionErrorMessage = null
                refreshUserData() // Ricarica per vedere lo stato aggiornato
            } else {
                actionErrorMessage = errorMsg
            }
            onResult(success)
        }
    }

    fun updateAnnuncio(annuncio: AnnuncioTurno, onResult: (Boolean) -> Unit) {
        viewModelScope.launch {
            isLoading = true
            try {
                val success = api.updateAnnuncio(annuncio.id ?: "", annuncio)
                onResult(success)
                if (success) refreshUserData()
            } catch (e: Exception) {
                onResult(false)
            } finally {
                isLoading = false
            }
        }
    }

    fun logout() {
        // Il dispositivo non deve più ricevere le notifiche dell'utente che esce.
        // La sessione si passa a parte: la chiamata parte dopo che il token è già stato tolto qui sotto.
        val sessione = FarmaFreelanceApi.token
        tokenPush?.let { (token, _) -> viewModelScope.launch { api.rimuoviDispositivoPush(token, sessione) } }
        FarmaFreelanceApi.token = null
        storage?.saveString("saved_token", "")
        currentUserRole = null
        currentUserEmail = null
        currentUserFarmacista = null
        currentUserFarmacia = null
        currentUserInfermiere = null
        
        // Pulizia credenziali salvate al logout esplicito
        if (!rememberMe) {
            storage?.saveString("saved_email", "")
            savedEmail = ""
        }
    }

    fun updateProfile(farmacista: Farmacista) {
        val email = currentUserEmail ?: return
        viewModelScope.launch {
            isLoading = true
            errorMessage = null
            try {
                val success = api.updateFarmacistaProfilo(email, farmacista)
                if (success) {
                    refreshUserData()
                } else {
                    errorMessage = "Errore durante l'aggiornamento del profilo"
                }
            } catch (e: Exception) {
                errorMessage = "Errore di connessione"
            } finally {
                isLoading = false
            }
        }
    }

    fun updateInfermiereProfile(infermiere: Infermiere) {
        val email = currentUserEmail ?: return
        viewModelScope.launch {
            isLoading = true
            errorMessage = null
            try {
                if (api.updateInfermiereProfilo(email, infermiere)) refreshUserData()
                else errorMessage = "Errore durante l'aggiornamento del profilo"
            } catch (e: Exception) {
                errorMessage = "Errore di connessione"
            } finally {
                isLoading = false
            }
        }
    }

    fun updateFarmaciaProfile(farmacia: Farmacia) {
        val email = currentUserEmail ?: return
        viewModelScope.launch {
            isLoading = true
            errorMessage = null
            try {
                val success = api.updateFarmaciaProfilo(email, farmacia)
                if (success) {
                    refreshUserData()
                } else {
                    errorMessage = "Errore durante l'aggiornamento del profilo"
                }
            } catch (e: Exception) {
                errorMessage = "Errore di connessione"
            } finally {
                isLoading = false
            }
        }
    }

    fun pubblicaAnnuncio(annuncio: AnnuncioTurno, onResult: (String) -> Unit = {}) {
        viewModelScope.launch {
            isLoading = true
            try {
                val result = api.createAnnuncio(annuncio)
                if (result == "CONFLICT") {
                    errorMessage = "Attenzione: Questo turno esiste già per lo stesso giorno e orario."
                } else if (result == "ERROR") {
                    errorMessage = "Impossibile salvare l'annuncio"
                }
                onResult(result)
            } catch (e: Exception) {
                errorMessage = "Impossibile salvare l'annuncio"
                onResult("ERROR")
            } finally {
                isLoading = false
            }
        }
    }

    fun pubblicaBatchAnnunci(request: BatchAnnuncioRequest, onResult: (String) -> Unit = {}) {
        viewModelScope.launch {
            isLoading = true
            try {
                val result = api.createBatchAnnunci(request)
                if (result == "CONFLICT") {
                    errorMessage = "Alcuni dei turni erano già presenti e sono stati saltati."
                } else if (result == "ERROR") {
                    errorMessage = "Errore durante la creazione dei turni a blocchi."
                }
                onResult(result)
            } catch (e: Exception) {
                errorMessage = "Impossibile salvare il blocco di annunci"
                onResult("ERROR")
            } finally {
                isLoading = false
            }
        }
    }

    fun registerFarmacista(farmacista: Farmacista) {
        viewModelScope.launch {
            isLoading = true
            errorMessage = null
            try {
                val success = api.registerFarmacista(farmacista)
                if (success) {
                    registrationSuccess = true
                } else {
                    errorMessage = "Registrazione fallita. Controlla i dati o riprova più tardi."
                }
            } catch (e: Exception) {
                errorMessage = "Errore di connessione: ${e.message}"
            } finally {
                isLoading = false
            }
        }
    }

    fun registerInfermiere(infermiere: Infermiere) {
        viewModelScope.launch {
            isLoading = true
            errorMessage = null
            try {
                when (api.registerInfermiere(infermiere)) {
                    "OK" -> registrationSuccess = true
                    "EMAIL_USATA" -> errorMessage = "Questa email è già registrata: accedi o usa un'altra email."
                    else -> errorMessage = "Registrazione fallita. Controlla i dati o riprova più tardi."
                }
            } catch (e: Exception) {
                errorMessage = "Errore di connessione: ${e.message}"
            } finally {
                isLoading = false
            }
        }
    }

    fun registerFarmacia(farmacia: Farmacia) {
        viewModelScope.launch {
            isLoading = true
            errorMessage = null
            try {
                val success = api.registerFarmacia(farmacia)
                if (success) {
                    registrationSuccess = true
                } else {
                    errorMessage = "Errore: Dati già presenti o non validi."
                }
            } catch (e: Exception) {
                errorMessage = "Il server non risponde. Riprova tra qualche istante."
            } finally {
                isLoading = false
            }
        }
    }

    suspend fun getAnnunciUtente(): List<AnnuncioTurno> {
        return api.getAnnunci(email = currentUserEmail)
    }

    fun eliminaAnnuncio(id: String, onResult: (Boolean) -> Unit) {
        viewModelScope.launch {
            val success = api.deleteAnnuncio(id)
            onResult(success)
        }
    }

    fun eliminaBatchAnnunci(ids: List<String>, onResult: (Boolean) -> Unit) {
        viewModelScope.launch {
            val success = api.deleteBatchAnnunci(ids)
            onResult(success)
        }
    }

    suspend fun getAnnuncioByIdSuspend(id: String): AnnuncioTurno? {
        return api.getAnnuncioById(id)
    }

    fun getAnnuncioById(id: String, onResult: (AnnuncioTurno?) -> Unit) {
        viewModelScope.launch {
            val annuncio = api.getAnnuncioById(id)
            onResult(annuncio)
        }
    }

    fun getFarmacistaByEmail(email: String, onResult: (Farmacista?) -> Unit) {
        viewModelScope.launch {
            val farmacista = api.getFarmacistaByEmail(email)
            onResult(farmacista)
        }
    }

    fun getFarmaciaByEmail(email: String, onResult: (Farmacia?) -> Unit) {
        viewModelScope.launch {
            val farmacia = api.getFarmaciaByEmail(email)
            onResult(farmacia)
        }
    }

    fun getFarmacistaById(id: String, onResult: (Farmacista?) -> Unit) {
        viewModelScope.launch {
            val farmacista = api.getFarmacistaById(id)
            onResult(farmacista)
        }
    }

    fun getInfermiereById(id: String, onResult: (Infermiere?) -> Unit) {
        viewModelScope.launch { onResult(api.getInfermiereById(id)) }
    }

    fun getFarmaciaById(id: String, onResult: (Farmacia?) -> Unit) {
        viewModelScope.launch {
            val farmacia = api.getFarmaciaById(id)
            onResult(farmacia)
        }
    }

    // --- LOGICA PREFERITI ---
    fun togglePreferito(targetId: String, targetRuolo: String) {
        val myId = currentUserFarmacia?.userId ?: currentUserFarmacista?.userId ?: currentUserInfermiere?.id
        val myEmail = currentUserEmail
        
        println("DEBUG: Richiesta togglePreferito")
        println("DEBUG: myId=$myId, myEmail=$myEmail, targetId=$targetId, targetRuolo=$targetRuolo")
        
        val idToUse = myId ?: myEmail
        
        if (idToUse == null) {
            println("DEBUG: ERRORE - Nessun identificativo utente trovato (ID ed Email nulli)")
            return
        }

        viewModelScope.launch {
            println("DEBUG: Eseguo chiamata API togglePreferito per $idToUse -> $targetId")
            try {
                val success = api.togglePreferito(Preferito(
                    utenteId = idToUse,
                    targetId = targetId,
                    targetRuolo = targetRuolo
                ))
                if (success) {
                    currentPreferiti = api.getPreferiti(idToUse)
                    println("DEBUG: Toggle riuscito, preferiti aggiornati (totale: ${currentPreferiti.size})")
                } else {
                    println("DEBUG: Il server ha risposto FALSE al toggle")
                }
            } catch (e: Exception) {
                println("DEBUG: Eccezione durante togglePreferito: ${e.message}")
            }
        }
    }

    fun isFavorite(targetId: String): Boolean {
        return currentPreferiti.any { it.targetId == targetId }
    }

    /**
     * Turni urgenti in home: quelli APERTI delle controparti nella zona dell'utente.
     * Senza email: con l'email il server restituirebbe i PROPRI annunci, non quelli delle controparti.
     */
    suspend fun getEmergencyAnnunci(): List<AnnuncioTurno> {
        val role = currentUserRole ?: return emptyList()
        if (role.isProfessionista) return getAnnunciControparte(zonaUtente).orEmpty()
        // La farmacia vede le disponibilità di farmacisti e infermieri.
        // distinctBy: un server che non conosce ancora "professione" rimanda due volte gli stessi turni.
        return Professione.entries.flatMap { getAnnunciControparte(zonaUtente, it).orEmpty() }.distinctBy { it.id }.sortedBy { it.dataTurno }
    }

    /**
     * Per Farmy: i turni aperti delle controparti. La farmacia riceve le disponibilità di farmacisti
     * e infermieri insieme; null se la rete non risponde.
     */
    suspend fun annunciControparti(provincia: String?): List<AnnuncioTurno>? {
        val role = currentUserRole ?: return emptyList()
        if (role.isProfessionista) return getAnnunciControparte(provincia)
        val perProfessione = Professione.entries.map { getAnnunciControparte(provincia, it) ?: return null }
        return perProfessione.flatten().distinctBy { it.id }.sortedBy { it.dataTurno }
    }

    /**
     * Per l'assistente: turni aperti delle controparti; null se la rete non risponde. Per la
     * farmacia [professione] sceglie di chi vedere le disponibilità (default farmacisti).
     */
    suspend fun getAnnunciControparte(provincia: String?, professione: Professione? = null): List<AnnuncioTurno>? {
        val role = currentUserRole ?: return emptyList()
        return api.getAnnunciControparte(role.name, provincia, professione)
    }

    /** Zona dei turni "vicino a me": province operative del professionista o provincia della farmacia. */
    val zonaUtente: String?
        get() {
            val farmacista = currentUserProfessionista
            val zona = if (farmacista != null) {
                farmacista.provinceOperativita.filter { it.isNotBlank() }.joinToString(",")
                    .ifBlank { farmacista.provinciaOperativita.orEmpty() }
            } else {
                currentUserFarmacia?.provincia.orEmpty()
            }
            return zona.ifBlank { null }
        }

    /** Coordinate dell'indirizzo del profilo (per le emergenze "vicino a me" di Farmy); null se non note. */
    val posizioneUtente: Pair<Double, Double>?
        get() {
            val f = currentUserProfessionista
            val lat = f?.lat ?: currentUserFarmacia?.lat
            val lng = f?.lng ?: currentUserFarmacia?.lng
            return if (lat != null && lng != null) lat to lng else null
        }

    suspend fun getAnnunciFarmacia(email: String): List<AnnuncioTurno> {
        // Parametro con nome: il primo posizionale di getAnnunci è il ruolo, non l'email.
        return api.getAnnunci(email = email).filter { it.stato == AnnuncioStato.APERTO }
    }

    suspend fun searchFarmacisti(
        query: String = "",
        comune: String = "",
        cap: String = "",
        startDate: Long? = null,
        endDate: Long? = null,
        competenza: String = "",
        provincia: String = "",
        regione: String = ""
    ): List<Farmacista> {
        return try {
            api.searchFarmacisti(query, comune, cap, startDate, endDate, currentUserEmail, competenza, provincia, regione)
        } catch (e: Exception) {
            emptyList()
        }
    }

    suspend fun searchInfermieri(
        query: String = "",
        comune: String = "",
        startDate: Long? = null,
        endDate: Long? = null,
        competenza: String = "",
        provincia: String = "",
        regione: String = "",
        cap: String = ""
    ): List<Infermiere> = try {
        api.searchInfermieri(query, comune, provincia, startDate, endDate, currentUserEmail, competenza, regione, cap)
    } catch (e: Exception) {
        emptyList()
    }

    suspend fun searchFarmacie(
        query: String = "",
        regione: String = "",
        provincia: String = "",
        comune: String = "",
        cap: String = "",
        startDate: Long? = null,
        endDate: Long? = null
    ): List<Farmacia> {
        return try {
            api.searchFarmacie(query, regione, provincia, comune, cap, startDate, endDate, currentUserEmail)
        } catch (e: Exception) {
            emptyList()
        }
    }

    suspend fun getStorico(): List<StoricoEntry> {
        val role = currentUserRole ?: return emptyList()
        val myId = mioId ?: return emptyList()
        
        return try {
            api.getStorico(myId, role.name)
        } catch (e: Exception) {
            emptyList()
        }
    }

    /** Per i guadagni: null se il profilo non c'è o la rete non risponde (non "nessun turno"). */
    suspend fun getStoricoOppureNull(): List<StoricoEntry>? {
        val role = currentUserRole ?: return null
        val myId = mioId ?: return null
        return api.getStoricoOppureNull(myId, role.name)
    }

    /** Agenda dei pagamenti: segna o toglie "pagato" su alcuni turni svolti del professionista. */
    suspend fun segnaPagato(ids: List<String>, pagato: Boolean, dataMillis: Long? = null): Boolean {
        val fId = currentUserProfessionista?.id ?: return false
        val role = currentUserRole ?: return false
        return api.segnaPagato(fId, ids, pagato, dataMillis, role.name)
    }

    suspend fun creaFatturaProforma(annuncioIds: List<String>, farmaciaId: String): FatturaEntry? {
        val fId = currentUserProfessionista?.id ?: return null
        val role = currentUserRole ?: return null
        return api.creaFatturaProforma(annuncioIds, fId, farmaciaId, role.name)
    }

    suspend fun getStoricoFatture(): List<FatturaEntry> {
        val myId = mioId ?: return emptyList()
        val role = currentUserRole ?: return emptyList()
        return api.getStoricoFatture(myId, role.name)
    }
}
